import { createReceiptCashDrawerOpener } from 'utility/receiptCashDrawer.util'
import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, Button, Card, Form, Dropdown } from 'react-bootstrap'
import ReactCountryFlag from 'react-country-flag'
import Swal from 'sweetalert2'

import DocumentInfo from './DocumentInfo'
import TabsDetail from './TabsDetail'
import FinancialSummary from './FinancialSummary'
import PaymentType from './PaymentType'
import PaymentMethod from './PaymentMethod'
import NhsoClaimModal from '../NhsoCloseBenefit/NhsoClaimModal'
import { currencyConvert } from '../../../../../utility/convert.util'
import httpClient from 'utility/httpClient.util'
import useAuth from 'hooks/useAuth'
import CashShiftSelectModal from '../CashManagement/CashShiftSelectModal'

import '../style.css'

const isConfigEnabled = (value) =>
  value === true || value === 1 || value === '1' || String(value).trim().toLowerCase() === 'true'

const Invoice = ({ match }) => {
  const { getJsonConfig } = useAuth()
  const receiptFormConfig = getJsonConfig('finance_receipt_form') || {}
  const isReceiptEdcEnabled = isConfigEnabled(receiptFormConfig.enableEdc)
  const isCashManagementEnabled = isConfigEnabled(receiptFormConfig.enableCashManagement)
  const [modal, setModal] = useState(false)
  const [nhsoModal, setNhsoModal] = useState(false)
  const [invoice, setInvoice] = useState()
  const [cloneInvoice, setCloneInvoice] = useState()
  const [cashShiftAmount, setCashShiftAmount] = useState(null)
  const receiptCashDrawerOpenerRef = useRef(null)
  if (!receiptCashDrawerOpenerRef.current) {
    receiptCashDrawerOpenerRef.current = createReceiptCashDrawerOpener()
  }
  const cashShiftResolveRef = useRef(null)
  const finishCashShift = (drawerId) => {
    cashShiftResolveRef.current?.(drawerId)
    cashShiftResolveRef.current = null
    setCashShiftAmount(null)
  }

  const invoiceId = match.params?.id

  const handleChange = (field, value) => {
    setInvoice({
      ...invoice,
      bllTo: {
        ...invoice?.bllTo,
        [field]: value,
      },
    })
  }

  const onPaymentChange = (field, value) => {
    setInvoice({
      ...invoice,
      paymentType: {
        ...invoice?.paymentType,
        [field]: value,
      },
    })
  }
  const getInvoice = async () => {
    await httpClient
      .get(`/finance/invoice/payment/${invoiceId}`)
      .then(({ data }) => {
        if (data.data) {
          setInvoice(data.data)
          setCloneInvoice(data.data)
        }
      })
      .catch((error) => {
        setInvoice()
        setCloneInvoice()
      })
  }
  useEffect(() => {
    if (invoiceId) {
      getInvoice()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onRestore = () => {
    const chargeCat = cloneInvoice?.chargeCategory
    setInvoice({
      ...invoice,
      chargeCategory: chargeCat,
    })
  }
  // Called by the EDC payment modal once the terminal approvals are captured.
  // Merge the EDC-built paymentType (incl. `edc` detail + paidByQrAmount) back
  // into the invoice, close the modal, and issue the receipt with that payload.
  const onConfirmPayment = (paymentType) => {
    setInvoice((prev) => ({ ...prev, paymentType }))
    setModal(false)
    onSave(paymentType)
  }

  const onSave = async (paymentTypeOverride) => {
    const paymentType =
      paymentTypeOverride && !paymentTypeOverride?.nativeEvent
        ? paymentTypeOverride
        : invoice?.paymentType
    // The receipt-issue endpoint is keyed by financeQueueId (NOT invoiceId) and
    // expects `chargeCategoryItems` (PaidChargeCategoryItemDto: chargeCategoryCode
    // + 5 numeric amount buckets). Map the loaded invoice to that exact shape.
    const financeQueueId = invoice?.financeQueueId
    const chargeCategoryItems = (invoice?.chargeCategory || []).map((item) => ({
      chargeCategoryCode:
        item?.chargeCategoryCode ?? item?.chargeCategory?.chargeCategoryCode ?? '',
      selfPayWithInsuranceAmount: Number(item?.selfPayWithInsuranceAmount || 0),
      insuranceAmount: Number(item?.insuranceAmount || 0),
      selfPayWithoutInsuranceAmount: Number(
        item?.selfPayWithoutInsuranceAmount || 0,
      ),
      arrearAmount: Number(item?.arrearAmount || 0),
      discountAmount: Number(item?.discountAmount || 0),
    }))
    let body = {
      bookNumber: invoice?.bookNumber,
      billNumber: invoice?.billNumber ?? '',
      isEdcRequired: false,
      paymentType: paymentType,
      chargeCategoryItems: chargeCategoryItems,
    }

    if (!financeQueueId) {
      Swal.fire({
        text: 'ไม่พบ financeQueueId ของใบนี้ — โหลดใบใหม่อีกครั้ง',
        icon: 'error',
        confirmButtonColor: '#7367f0',
        confirmButtonText: 'ตกลง',
      })
      return
    }

    const cashAmount = Number(paymentType?.paidByCashAmount || 0)
    const cashDrawerId = cashAmount > 0 && isCashManagementEnabled ? await new Promise((resolve) => {
      cashShiftResolveRef.current = resolve
      setCashShiftAmount(cashAmount)
    }) : null
    if (cashAmount > 0 && isCashManagementEnabled && !cashDrawerId) return
    if (cashDrawerId) body.cashDrawerId = cashDrawerId

    await httpClient
      .post(`/finance/receipt/issue/${financeQueueId}`, body)
      .then(async (response) => {
        await receiptCashDrawerOpenerRef.current({
          config: receiptFormConfig,
          cashAmount,
          response,
          financeQueueId,
        })
        await Swal.fire({
          title: 'ออกใบเสร็จสำเร็จ',
          icon: 'success',
          confirmButtonColor: '#7367f0',
          confirmButtonText: 'ตกลง',
        })
        window.location.reload()
      })
      .catch((error) => {
        Swal.fire({
          text: `${error?.response?.data?.message || error}`,
          icon: 'error',
          confirmButtonColor: '#7367f0',
          confirmButtonText: 'ตกลง',
        })
      })
  }

  return (
    <>
      <CashShiftSelectModal show={cashShiftAmount !== null} amount={cashShiftAmount} referenceNo={invoice?.financeQueueId}
        onConfirm={finishCashShift} onCancel={() => finishCashShift(null)} />
      <Card>
        <Card.Body>
          <Row>
            <Col className="d-flex justify-content-end">
              <Button className="btn-icon mx-1" variant="success">
                <i className="fas fa-share mx-1" />
              </Button>
              <Button className="btn-icon mx-1" variant="primary">
                <i className="fas fa-download mx-1" />
              </Button>
              <Button className="btn-icon mx-1" variant="danger">
                <i className="fas fa-print mx-1" />
              </Button>

              <Dropdown>
                <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                  <i class="fas fa-ellipsis-v"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="cancel">ยกเลิกใบเสร็จ</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>

          <Row>
            <Col sm="12" className="my-4 d-flex justify-content-end">
              <p>
                <span className="mx-1 lead">จำนวนเงินรวมทั้งสิ้น</span>

                <span className="text-primary display-3 mx-1">
                  {currencyConvert(invoice?.summaryCharge?.totalAmount)}
                </span>
                <span className="mx-1 lead">สกุลเงิน</span>
                <ReactCountryFlag
                  className="country-flag"
                  countryCode="th"
                  style={{
                    fontSize: '2em',
                    lineHeight: '2em',
                    marginLeft: 5,
                    marginBottom: 10,
                  }}
                  svg
                />
                <span className="mx-1 lead">THB - ไทย</span>
              </p>
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <DocumentInfo invoice={invoice} handleChange={handleChange} />
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <TabsDetail
                invoice={invoice}
                setInvoice={setInvoice}
                onRestore={onRestore}
              />
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <FinancialSummary invoice={invoice} />
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <PaymentType
                invoice={invoice}
                onPaymentChange={onPaymentChange}
              />
            </Col>
          </Row>
          <Row>
            <Col sm="12" className="d-flex justify-content-end">
              <Form.Group as={Row} className="mb-2">
                <Form.Label column sm={5} className="text-right pr-0">
                  มียอดใบเสร็จค้างชำระ
                </Form.Label>
                <Col sm={7}>
                  <Form.Control
                    type="text"
                    value={currencyConvert(invoice?.totalInArrearAmount)}
                    readOnly
                    className="text-right bg-danger text-white border-none"
                  />
                </Col>
              </Form.Group>
            </Col>
          </Row>
          <Row className="d-flex justify-content-between">
            <Col>
              <Form.Group className="d-flex">
                <Button
                  className="mb-0"
                  variant="outline-info"
                  onClick={() => setModal(!modal)}
                >
                  <i className="feather icon-navigation mx-1" />
                  รับเงิน / ส่งข้อมูล EDC
                </Button>
                <Button
                  className="mb-0 ml-2"
                  variant="outline-primary"
                  onClick={() => setNhsoModal(true)}
                  disabled={!invoice?.vn}
                >
                  <i className="feather icon-shield mx-1" />
                  ปิดสิทธิ์ สปสช.
                </Button>
              </Form.Group>
            </Col>
            <Col className="d-flex justify-content-end">
              <Form inline>
                <Form.Group>
                  <Button className="mb-0" variant="outline-secondary">
                    <i className="feather icon-chevron-left mx-1" />
                    กลับตาราง
                  </Button>
                </Form.Group>
                <Form.Group>
                  <Button
                    className="mb-0 mx-2"
                    variant="primary"
                    onClick={onSave}
                  >
                    <i className="feather icon-save mx-1" />
                    บันทึก
                  </Button>
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <PaymentMethod
        show={modal}
        onHide={() => setModal(false)}
        invoice={invoice}
        edcConnectUrl={receiptFormConfig.edcConnectUrl}
        qrBankAccountNumber={receiptFormConfig.qrBankAccountNumber}
        creditBankAccountNumber={receiptFormConfig.creditBankAccountNumber}
        totalSelfPayment={invoice?.summaryCharge?.totalPayAmount}
        financeQueueId={invoice?.financeQueueId}
        onConfirm={onConfirmPayment}
      />

      <NhsoClaimModal
        show={nhsoModal}
        onHide={() => setNhsoModal(false)}
        hn={invoice?.hn}
        vn={invoice?.vn}
        financeQueueId={invoice?.financeQueueId}
      />
    </>
  )
}

export default Invoice
